import { T } from '@tolgee/react';
import { useState } from 'react';
import styled from 'styled-components';

import { MaterialAttachment } from '../../../../hooks/useUploadMaterialAttachment';
import GameImageRenderer, {
  OtherFileRenderer,
  TextFileRenderer,
} from '../../../_molecules/GameImageRenderer/GameImageRenderer';
import { MaterialAttachmentPreview } from '../MaterialAttachmentPreview';
import { AttachmentPreview, BadgeText, TextFileBadge } from '../MaterialAttachments';

export function ChatAttachments({
  attachments,
}: {
  attachments: Array<MaterialAttachment>;
}) {
  const [isAttachmentPreviewOpen, setIsAttachmentPreviewOpen] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState<MaterialAttachment>();

  return (
    <ChatAttachmentsGrid>
      <MaterialAttachmentPreview
        isAttachmentPreviewOpen={isAttachmentPreviewOpen}
        setIsAttachmentPreviewOpen={setIsAttachmentPreviewOpen}
        selectedAttachment={selectedAttachment}
      />
      {attachments.map(file => {
        const { unprocessedAttachment, ...attachment } = file;

        // we may have processed their docx into markdown, but we still want to display the attachment as docx
        // so we need to display the unprocessed attachment if it exists
        const displayAttachment = unprocessedAttachment ?? attachment;
        return (
          <ChatAttachmentContainer
            key={file.fileId}
            onClick={() => {
              setSelectedAttachment(displayAttachment);
              setIsAttachmentPreviewOpen(true);
            }}
          >
            {displayAttachment.mimetype.startsWith('image/') && (
              <AttachmentPreview>
                <GameImageRenderer
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                  fileId={displayAttachment.fileId}
                  fileMimetype={displayAttachment.mimetype}
                  fileAltText={''}
                  isDiscoverTemplate={false}
                  isAuthenticated
                  isOwner // it should be impossible to not be owner and authenticated
                />
              </AttachmentPreview>
            )}
            {displayAttachment.mimetype === 'text/plain' &&
              displayAttachment.attachmentMethod === 'paste' && (
                <AttachmentPreview>
                  <TextFileRenderer fileId={displayAttachment.fileId} />
                  <TextFileBadge>
                    <BadgeText>
                      <T keyName="Pasted" defaultValue="Pasted" />
                    </BadgeText>
                  </TextFileBadge>
                </AttachmentPreview>
              )}
            {displayAttachment.mimetype === 'text/plain' &&
              displayAttachment.attachmentMethod === 'upload' && (
                <AttachmentPreview>
                  <TextFileRenderer fileId={displayAttachment.fileId} />
                </AttachmentPreview>
              )}
            {displayAttachment.mimetype !== 'text/plain' &&
              displayAttachment.attachmentMethod === 'upload' &&
              !displayAttachment.mimetype.startsWith('image') && (
                <AttachmentPreview>
                  <OtherFileRenderer
                    fileId={displayAttachment.fileName}
                    fileMimetype={displayAttachment.mimetype}
                  />
                </AttachmentPreview>
              )}
          </ChatAttachmentContainer>
        );
      })}
    </ChatAttachmentsGrid>
  );
}

// Chat History Attachment Components
const ChatAttachmentsGrid = styled.div`
  display: grid;
  width: 100%;
  gap: 10px;
  height: 90px;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  grid-template-rows: 90px;
  position: relative;
  direction: rtl;
`;

const ChatAttachmentContainer = styled.div`
  width: 100%;
  height: 100%;
  cursor: pointer;
  position: relative;
`;
